import React, { memo, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import { resolveEvacuationLine3 } from '../../utils/utils';

const TITLE_FONT_SIZE = 16;
const HEADING_FONT_SIZE = 11;
const BODY_FONT_SIZE = 10;

// Site times are stored as 24H strings ("15:30" / "15:30:00"); the report shows 12H.
const formatTime12H = (time: any) => {
    if (!time) return '';
    const parsed = moment(time, ['HH:mm:ss', 'HH:mm', 'hh:mm A', 'hh:mmA'], true);
    return parsed.isValid() ? parsed.format('hh:mm A') : `${time}`;
};

const SiteInfoReportPDF = ({ data, setPdfBlob }: { data: any, setPdfBlob: any }) => {
    const todayDate = moment().format('MM-DD-YYYY');

    // data = data.slice(0, 500)

    // Group and sort the data by DIST_NAM and SITE_NAM
    const groupByDistrict = (data: any) => {
        const grouped = data.reduce((acc: any, item: any) => {
            const key = item.DIST_NAM;
            if (!acc[key]) {
                acc[key] = {
                    DIST_NAM: item.DIST_NAM,
                    items: []
                };
            }
            acc[key].items.push(item);
            return acc;
        }, {});

        // Sort districts alphabetically
        const sortedDistricts = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

        // Return sorted data with sorted site names within each district
        return sortedDistricts.map(districtKey => {
            const district = grouped[districtKey];
            district.items = groupBySiteName(district.items); // Sort site names within each district
            return district;
        });
    };

    const groupBySiteName = (items: any[]) => {
        const grouped = items.reduce((acc, item) => {
            const key = item.SITE_NAM;
            if (!acc[key]) {
                acc[key] = {
                    SITE_NAM: item.SITE_NAM,
                    siteItems: [],
                };
            }
            acc[key].siteItems.push(item);
            return acc;
        }, {});

        // Sort site names alphabetically
        return Object.keys(grouped).sort((a, b) => a.localeCompare(b)).map(siteKey => grouped[siteKey]);
    };

    const generatePDF = () => {
        let index = 0;
        try {
            const doc = new jsPDF('portrait', 'mm', 'a4');
            const pageWidth = 210; // A4 width
            const pageHeight = 297; // A4 height
            const margin = 10;
            const usableWidth = pageWidth - 2 * margin;
            const lineHeight = 5;
            let yPos = 20; // Initial vertical position for content

            const groupedData: any = groupByDistrict(data); // Grouping data by district
            const rowHeight = 30;  // Height for each row (increased a bit for content)

            // Used to avoid a trailing blank page after the final site
            const totalSites = groupedData.reduce((count: number, district: any) => count + district.items.length, 0);
            let renderedSites = 0;

            const pageBottomY = pageHeight - margin * 2;

            // Set once per district; the header below is drawn once per site, not per
            // page, so a site's continuation pages carry no title or district
            let currentDistrictLine = '';

            const drawReportHeader = () => {
                // Title
                doc.setFontSize(TITLE_FONT_SIZE);
                doc.text('Site Information', 14, yPos);

                // Horizontal line below the title (HR-like line).
                yPos += 2.5
                doc.setLineWidth(0.5); // Set the thickness of the line
                doc.line(14, yPos, 200, yPos); // Draw the line (X1, Y1, X2, Y2)

                // District and Program Info
                doc.setFontSize(BODY_FONT_SIZE);

                // District and Program on separate lines
                yPos += 5.5
                doc.text(currentDistrictLine, 14, yPos);
            };

            const checkPageEnd = (currentYPos: number, additionalHeight = 0) => {
                if (currentYPos + additionalHeight + rowHeight >= pageBottomY) {
                    doc.addPage();
                    return margin + lineHeight; // Reset Y position for new page
                }
                return currentYPos;
            };

            const checkNotesPageEnd = (currentYPos: number) => {
                if (currentYPos + lineHeight >= pageBottomY) {
                    doc.addPage();
                    return margin + lineHeight;
                }
                return currentYPos;
            };

            const notesContentWidth = pageWidth - 28; // 14mm left + right margins
            const minNotesContentHeight = 20; // mm reserved for notes even when empty

            // Wrap a table cell's values to the column width so nothing runs into the
            // next column. Blank values are skipped.
            const wrapCellLines = (values: any[], maxWidth: number) => {
                const lines: string[] = [];
                values.forEach((value) => {
                    const text = value === null || value === undefined ? '' : `${value}`;
                    if (!text.trim()) return;
                    doc.splitTextToSize(text, maxWidth).forEach((line: string) => lines.push(line));
                });
                return lines;
            };

            const drawCellLines = (lines: string[], x: number, topY: number) => {
                lines.forEach((line, i) => {
                    doc.text(line, x, topY + (i + 1) * lineHeight);
                });
            };

            const drawWrappedText = (text: string, x: number) => {
                // Normalize line endings, then split into text and newline runs
                const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
                const tokens = normalized.split(/(\n+)/);
                let hasDrawnLine = false;

                tokens.forEach((token) => {
                    if (!token) return;

                    // Newline run: single \n = next line (no extra gap), 2+ \n = blank line(s)
                    if (/^\n+$/.test(token)) {
                        if (!hasDrawnLine) return;
                        const extraGap = (token.length - 1) * lineHeight;
                        if (extraGap > 0) {
                            yPos += extraGap;
                            yPos = checkNotesPageEnd(yPos);
                        }
                        return;
                    }

                    const content = token.trim();
                    if (!content) return;

                    const lines = doc.splitTextToSize(content, notesContentWidth);
                    lines.forEach((line: string) => {
                        yPos = checkNotesPageEnd(yPos);
                        doc.text(line, x, yPos);
                        yPos += lineHeight;
                        hasDrawnLine = true;
                    });
                });
            };

            groupedData.forEach((district: any) => {
                const { items, DIST_NAM } = district;

                currentDistrictLine = `District: ${items[0]?.siteItems[0]?.DIST_NUM} ${items[0]?.siteItems[0]?.DIST_NAM}`;

                items.forEach((site: any) => {
                    const { siteItems, SITE_NAM } = site;

                    drawReportHeader();

                    yPos += 5
                    doc.text(`Program: ${siteItems[0]?.SITE_NUM} ${siteItems[0]?.SITE_NAM}`, 14, yPos);

                    // Hours and Permit# on the same line
                    yPos += 5
                    doc.text(`Hours: ${formatTime12H(siteItems[0]?.START_TIME)} - ${formatTime12H(siteItems[0]?.STOP_TIME)}`, 14, yPos);
                    doc.text(`Permit#: ${siteItems[0]?.PERMIT}`, 125, yPos);

                    // Start Date and siteItems Liaison# on the same line
                    const startDate = siteItems[0]?.START_DATE ? moment(siteItems[0]?.START_DATE).format('MM-DD-YYYY') : '';
                    yPos += 5
                    doc.text(`Start: ${startDate}`, 14, yPos);
                    doc.text(`District Liaison#: ${siteItems[0]?.LIAISON}`, 125, yPos);

                    // Scope Email and Address
                    yPos += 5
                    doc.text(`Scope Email: ${siteItems[0]?.ScopeEmail}`, 14, yPos);
                    yPos += 5
                    doc.text(`${siteItems[0]?.ADDR1}`, 40, yPos);
                    yPos += 5
                    doc.text(`${siteItems[0]?.ADDR2}`, 40, yPos);
                    yPos += 5
                    doc.text(`${siteItems[0]?.ADDR3}`, 40, yPos);
                    doc.text(`Principal Name: ${siteItems[0]?.PRINCIPAL}`, 125, yPos - 15);
                    doc.text(`Principal Email: ${siteItems[0]?.PEMAIL ?? ''}`, 125, yPos - 10);

                    // School Phone and Fax
                    yPos += 5
                    doc.text(`School Phone:  ${siteItems[0]?.SCHFONE}`, 14, yPos);
                    doc.text(`School Fax: ${siteItems[0]?.SFAX}`, 80, yPos);
                    doc.text(`Ambulance: ${siteItems[0]?.AMBPHONE}`, 125, yPos);

                    // Evacuation Location #1 and #2, side by side
                    doc.setFontSize(HEADING_FONT_SIZE);
                    yPos += 10
                    doc.setFont('helvetica', 'bold');
                    doc.text('Evacuation 1', 14, yPos);
                    doc.text('Evacuation 2', 100, yPos);
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(BODY_FONT_SIZE);

                    // Blank fields are skipped so the block stays compact.
                    const evacuationHeadingY = yPos;
                    const evacuation1 = [
                        siteItems[0]?.EVAC_LOC_1,
                        siteItems[0]?.EADR1,
                        siteItems[0]?.EADR2,
                        resolveEvacuationLine3(
                            siteItems[0]?.EVAC_LOC_1,
                            siteItems[0]?.EADR1,
                            siteItems[0]?.EADR2,
                            siteItems[0]?.EADR3
                        ),
                        siteItems[0]?.EPHONE ? `Phone: ${siteItems[0]?.EPHONE}` : '',
                    ].filter((line: any) => line);
                    const evacuation2 = [
                        siteItems[0]?.EVAC_LOC_2,
                        siteItems[0]?.EADR21,
                        siteItems[0]?.EADR22,
                        resolveEvacuationLine3(
                            siteItems[0]?.EVAC_LOC_2,
                            siteItems[0]?.EADR21,
                            siteItems[0]?.EADR22,
                            siteItems[0]?.EADR23
                        ),
                        siteItems[0]?.EPHONE2 ? `Phone: ${siteItems[0]?.EPHONE2}` : '',
                    ].filter((line: any) => line);

                    evacuation1.forEach((line: any, i: number) => {
                        doc.text(`${line}`, 14, evacuationHeadingY + (i + 1) * lineHeight);
                    });
                    evacuation2.forEach((line: any, i: number) => {
                        doc.text(`${line}`, 100, evacuationHeadingY + (i + 1) * lineHeight);
                    });

                    // Keep at least the original 3-line height so the sections below don't shift up
                    yPos = evacuationHeadingY + Math.max(evacuation1.length, evacuation2.length, 3) * lineHeight;

                    doc.setFontSize(HEADING_FONT_SIZE);
                    yPos += 10
                    doc.setFont('helvetica', 'bold');
                    doc.text('Fire Department', 14, yPos);
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(BODY_FONT_SIZE);
                    yPos += 5
                    doc.text(`${siteItems[0]?.FADR1}`, 14, yPos);
                    yPos += 5
                    doc.text(`${siteItems[0]?.FADR2}`, 14, yPos);
                    yPos += 5
                    doc.text(`${siteItems[0]?.FADR3} ${siteItems[0]?.FPHONE}`, 14, yPos);

                    doc.setFontSize(HEADING_FONT_SIZE);
                    doc.setFont('helvetica', 'bold');
                    doc.text('Police Department', 100, yPos - 15);
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(BODY_FONT_SIZE);
                    doc.text(`${siteItems[0]?.PADR1}`, 100, yPos - 10);
                    doc.text(`${siteItems[0]?.PADR2}`, 100, yPos - 5);
                    doc.text(`${siteItems[0]?.PADR3} ${siteItems[0]?.PPHONE}`, 100, yPos);

                    doc.setFontSize(BODY_FONT_SIZE);
                    // doc.setFont('normal');

                    // New Section: Scope Cell Phone and Fees
                    doc.setFontSize(BODY_FONT_SIZE);
                    yPos += 20
                    // Scope Cell Phone and Fees header
                    doc.text('Scope Cell Phone:', 14, yPos);
                    doc.text(`${siteItems[0]?.PHONE}`, 50, yPos);

                    // Fees section: Full, Min, Daily, AM/PM headers
                    doc.text('Fees:', 90, yPos);
                    doc.text('Full', 110, yPos - 5);
                    doc.text('Min', 130, yPos - 5);
                    doc.text('Daily', 150, yPos - 5);
                    doc.text('AM/PM', 170, yPos - 5);

                    // Fees section values
                    doc.text(`${siteItems[0]?.FULL}`, 110, yPos); // Full
                    doc.text(`${siteItems[0]?.MIN}`, 130, yPos);  // Min
                    doc.text(`${siteItems[0]?.DAILY}`, 150, yPos);  // Daily
                    doc.text(`${siteItems[0]?.AMPM}`, 170, yPos);  // AM/PM



                    // New Section: Approved Spaces, Space Capacity, Additional Approved Spaces, Space Capacity
                    doc.setFontSize(HEADING_FONT_SIZE);
                    yPos += 10
                    doc.setFont('helvetica', 'bold');
                    doc.text('Approved Spaces', 14, yPos); // Title for Approved Spaces
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(BODY_FONT_SIZE);
                    yPos += 5
                    doc.text(`${siteItems[0]?.ROOM_NO}`, 14, yPos);
                    yPos += 5 // Room 1
                    doc.text(`${siteItems[0]?.ROOM_NO2}`, 14, yPos);
                    yPos += 5  // Room 2
                    doc.text(`${siteItems[0]?.ROOM_NO3}`, 14, yPos);
                    yPos += 5 // Room 3
                    doc.text(`${siteItems[0]?.ROOM_NO4}`, 14, yPos);  // Room 4

                    doc.setFontSize(HEADING_FONT_SIZE);
                    doc.setFont('helvetica', 'bold');
                    doc.text('Space Capacity', 60, yPos - 20);  // Title for Space Capacity
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(BODY_FONT_SIZE);
                    doc.text(`${siteItems[0]?.CAP1}`, 60, yPos - 15);  // Capacity 1
                    doc.text(`${siteItems[0]?.CAP2}`, 60, yPos - 10);  // Capacity 2
                    doc.text(`${siteItems[0]?.CAP3}`, 60, yPos - 5);  // Capacity 3
                    doc.text(`${siteItems[0]?.CAP4}`, 60, yPos);  // Capacity 4

                    doc.setFontSize(HEADING_FONT_SIZE);
                    doc.setFont('helvetica', 'bold');
                    doc.text('Additional Approved Spaces', 100, yPos - 20);  // Title for Additional Approved Spaces
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(BODY_FONT_SIZE);
                    doc.text(`${siteItems[0]?.ADDLOC}`, 100, yPos - 15);  // Additional location 1
                    doc.text(`${siteItems[0]?.ADDLOC2}`, 100, yPos - 10);  // Additional location 2
                    doc.text(`${siteItems[0]?.ADDLOC3}`, 100, yPos - 5);  // Additional location 3
                    doc.text(`${siteItems[0]?.ADDLOC4}`, 100, yPos);  // Additional location 4

                    doc.setFontSize(HEADING_FONT_SIZE);
                    doc.setFont('helvetica', 'bold');
                    doc.text('Space Capacity', 160, yPos - 20);  // Title for the second Space Capacity
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(BODY_FONT_SIZE);
                    doc.text(`${siteItems[0]?.ASCAP1}`, 160, yPos - 15);  // Second capacity 1
                    doc.text(`${siteItems[0]?.ASCAP2}`, 160, yPos - 10);  // Second capacity 2
                    doc.text(`${siteItems[0]?.ASCAP3}`, 160, yPos - 5);  // Second capacity 3
                    doc.text(`${siteItems[0]?.ASCAP4}`, 160, yPos);  // Second capacity 4

                    // License Capacity and Landline Location
                    yPos = checkPageEnd(yPos); // Check if new page is needed
                    yPos += 10
                    doc.text('License Capacity:', 14, yPos);
                    doc.text(`${siteItems[0]?.CAPACTIY}`, 60, yPos);
                    // The rows down to Ext share one value column so they line up. It stays
                    // at 140 unless a label runs past it - 'Landline phone location:' does -
                    // in which case every value in the column shifts out together.
                    const rightLabelX = 100;
                    const rightLabels = [
                        'Landline phone location:',
                        'OCFS Rep:',
                        'Transport:',
                        'Registrar:',
                        'Account Billing:',
                        'Ext:',
                    ];
                    const rightValueX = Math.max(
                        140,
                        rightLabelX + Math.max(...rightLabels.map((label) => doc.getTextWidth(label))) + 3
                    );

                    // LNDLNLOC is the Landline Location edited on the site's Additional
                    // Information tab. PHONE_TYPE, printed here before, is a legacy column
                    // no screen edits, so the report and the tab could never agree.
                    doc.text('Landline phone location:', rightLabelX, yPos);
                    // Landline locations run long (a room plus a phone number and hours),
                    // so wrap rather than let the text run off the right edge, and push the
                    // rows below down by however many extra lines that takes.
                    const landlineLines = doc.splitTextToSize(
                        `${siteItems[0]?.LNDLNLOC ?? ''}`,
                        pageWidth - margin - rightValueX
                    );
                    landlineLines.forEach((line: string, i: number) => {
                        doc.text(line, rightValueX, yPos + i * lineHeight);
                    });
                    yPos += Math.max(0, landlineLines.length - 1) * lineHeight;

                    // Grade and Scope Available
                    // yPos += 5;
                    // yPos = checkPageEnd(yPos);
                    // doc.text('Grade:', 14, yPos);
                    // doc.text(`${siteItems[0]?.GRADE_LVLS}`, 60, yPos);
                    // doc.text('Scope Available:', 100, yPos);
                    // doc.text(`${siteItems[0]?.TIME_AVAIL}`, 140, yPos);

                    // OCFS Phone and OCFS Rep
                    yPos += 5;
                    yPos = checkPageEnd(yPos);
                    doc.text('OCFS Phone:', 14, yPos);
                    doc.text(`${siteItems[0]?.DSS_FON}`, 60, yPos);
                    doc.text('OCFS Rep:', rightLabelX, yPos);
                    doc.text(`${siteItems[0]?.DSS_REP}`, rightValueX, yPos);

                    // Transport Phone and Transport
                    yPos += 5;
                    yPos = checkPageEnd(yPos);
                    doc.text('Transport Phone:', 14, yPos);
                    doc.text(`${siteItems[0]?.TPPHONE}`, 60, yPos);
                    doc.text('Transport:', rightLabelX, yPos);
                    doc.text(`${siteItems[0]?.TRANSPORT}`, rightValueX, yPos);

                    // District Manager and Registrar
                    yPos += 5;
                    yPos = checkPageEnd(yPos);
                    doc.text('District Field Manager:', 14, yPos);
                    doc.text(`${siteItems[0]?.FieldSupervisorName}`, 60, yPos);
                    doc.text('Registrar:', rightLabelX, yPos);
                    doc.text(`${siteItems[0]?.RegistarName}`, rightValueX, yPos);

                    // Scope Field Trainer and Account Billing
                    // yPos += 5;
                    // yPos = checkPageEnd(yPos);
                    // doc.text('Scope Field Trainer:', 14, yPos);
                    // doc.text(`${siteItems[0]?.FieldTrainerName}`, 60, yPos);
                    // doc.text('Account Billing:', 100, yPos);
                    // doc.text(`${siteItems[0]?.AccountBillingName}`, 140, yPos);

                    // HCC (Health Care Consultant) and Account Billing
                    yPos += 5;
                    yPos = checkPageEnd(yPos);
                    doc.text('HCC:', 14, yPos);
                    doc.text(`${siteItems[0]?.HealthCareConsultantName}`, 60, yPos);
                    doc.text('Account Billing:', rightLabelX, yPos);
                    doc.text(`${siteItems[0]?.AccountBillingName ?? ''}`, rightValueX, yPos);

                    // Account Billing extension
                    yPos += 5;
                    yPos = checkPageEnd(yPos);
                    doc.text('Ext:', rightLabelX, yPos);
                    doc.text(`${siteItems[0]?.AccountBillingExt ?? ''}`, rightValueX, yPos);

                    // Notes Section
                    yPos += 10; // Adjust Y position after the previous section
                    yPos = checkPageEnd(yPos); // Check if a new page is needed


                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(HEADING_FONT_SIZE);
                    doc.text('Notes:', 14, yPos);
                    doc.setFont('helvetica', 'normal');

                    yPos += 5;
                    doc.setFontSize(BODY_FONT_SIZE);

                    const notesContentStartY = yPos;
                    const notesStartPage = doc.getNumberOfPages();
                    let notesRendered = false;

                    if (siteItems && siteItems.length > 0) {
                        const renderedNotes = new Set<string>();
                        siteItems.forEach((x: any) => {
                            if (x.NOTES && !renderedNotes.has(x.NOTES)) {
                                renderedNotes.add(x.NOTES);
                                notesRendered = true;
                                drawWrappedText(x.NOTES, 14);
                            }
                        });
                    }

                    // Minimum space only on the same page as notes start (total height, not row-based)
                    const notesEndPage = doc.getNumberOfPages();
                    if (notesEndPage === notesStartPage) {
                        const notesBlockHeight = yPos - notesContentStartY;
                        const targetHeight = notesRendered
                            ? Math.max(notesBlockHeight, minNotesContentHeight)
                            : minNotesContentHeight;
                        yPos = notesContentStartY + targetHeight;
                    }
                    yPos = checkPageEnd(yPos);

                    // Staff List Section — position follows actual notes height (or minimum)
                    yPos += 5;

                    const columnWidths = [45, 50, 50, 45];  // Adjusted column widths to fit page width
                    const marginLeft = 14;  // Left margin of the document
                    const staffHeadingHeight = 5;

                    const drawStaffListHeading = () => {
                        doc.setFont('helvetica', 'bold');
                        doc.setFontSize(HEADING_FONT_SIZE);
                        doc.text('Staff List:', 14, yPos);
                        doc.setFont('helvetica', 'normal');
                        doc.setFontSize(BODY_FONT_SIZE);
                        yPos += staffHeadingHeight;
                    };

                    // Measured up front so the heading can reserve room for the first row.
                    // First column wraps, so it can need more than the 5 lines the other
                    // columns use. Size the row to whichever is taller.
                    const staffRows = (siteItems ?? []).map((x: any) => {
                        const detailLines = wrapCellLines(
                            [
                                [x.FIRSTNAME, x.LASTNAME].filter((part: any) => part).join(' '),
                                x.SitePos,
                                x.STREET,
                                [x.CITY, x.STATE, x.ZIPCODE].filter((part: any) => part).join(', '),
                            ],
                            columnWidths[0] - 4
                        );
                        return {
                            staff: x,
                            detailLines,
                            height: Math.max(rowHeight, (Math.max(detailLines.length, 5) + 1) * lineHeight),
                        };
                    });

                    // The table nearly always lands on a page of its own, so break before
                    // the heading whenever the first row would not fit under it: the
                    // heading has to travel with the rows rather than be stranded alone at
                    // the bottom of the page above them.
                    yPos = checkPageEnd(
                        yPos,
                        staffHeadingHeight + (staffRows.length > 0 ? staffRows[0].height - rowHeight : 0)
                    );
                    drawStaffListHeading();

                    staffRows.forEach((row: any) => {
                        const x = row.staff;
                        const staffDetailLines = row.detailLines;
                        const staffRowHeight = row.height;

                        const pageBeforeRow = doc.getNumberOfPages();
                        yPos = checkPageEnd(yPos, staffRowHeight - rowHeight); // Ensure page break if necessary
                        if (doc.getNumberOfPages() !== pageBeforeRow) {
                            drawStaffListHeading(); // The table carries on here, so label it again
                        }

                        // Define X positions for each column
                        const column1X = marginLeft;
                        const column2X = column1X + columnWidths[0];
                        const column3X = column2X + columnWidths[1];
                        const column4X = column3X + columnWidths[2];

                        // Draw the borders for each row
                        // Row Borders
                        doc.rect(column1X, yPos, columnWidths[0], staffRowHeight); // First column
                        doc.rect(column2X, yPos, columnWidths[1], staffRowHeight); // Second column
                        doc.rect(column3X, yPos, columnWidths[2], staffRowHeight); // Third column
                        doc.rect(column4X, yPos, columnWidths[3], staffRowHeight); // Fourth column

                        // First Column (Staff Details) - wrapped, since names, streets and
                        // city/state/zip lines regularly run wider than the column
                        drawCellLines(staffDetailLines, column1X + 2, yPos);

                        // Second Column (Phones)
                        doc.text('Home:', column2X + 2, yPos + 5);
                        doc.text(`${x.HOMEPHONE}`, column2X + 20, yPos + 5);
                        doc.text('Work:', column2X + 2, yPos + 10);
                        doc.text(`${x.WORKPHONE}`, column2X + 20, yPos + 10);
                        doc.text('Other:', column2X + 2, yPos + 15);
                        doc.text(`${x.OTHERPHONE}`, column2X + 20, yPos + 15);
                        doc.text('NYSID#:', column2X + 2, yPos + 20);
                        doc.text(`${x.NYSID}`, column2X + 20, yPos + 20);
                        doc.text('A. W. Children:', column2X + 2, yPos + 25);
                        doc.text(`${x.AloneWithChildren ? 'YES' : 'NO'}`, column2X + 30, yPos + 25);

                        // Third Column (Expiration Dates)
                        doc.text('Fingerprint:', column3X + 2, yPos + 5);
                        doc.text(`${x.STATE_FPR ? moment(x.STATE_FPR).format('MM-DD-YYYY') : ''}`, column3X + 28, yPos + 5);
                        doc.text('Medical:', column3X + 2, yPos + 10);
                        doc.text(`${x.MEDICALEXP ? moment(x.MEDICALEXP).format('MM-DD-YYYY') : ''}`, column3X + 28, yPos + 10);
                        doc.text('Foundations:', column3X + 2, yPos + 15);
                        doc.text(`${x.Foundations ? moment(x.Foundations).format('MM-DD-YYYY') : ''}`, column3X + 28, yPos + 15);
                        doc.text('CPR Expires:', column3X + 2, yPos + 20);
                        doc.text(`${x.CPR ? moment(x.CPR).format('MM-DD-YYYY') : ''}`, column3X + 28, yPos + 20);
                        doc.text('F. A. Expires:', column3X + 2, yPos + 25);
                        doc.text(`${x.FIRSTAID ? moment(x.FIRSTAID).format('MM-DD-YYYY') : ''}`, column3X + 28, yPos + 25);

                        // Fourth Column (Other Expirations)
                        doc.text('Mat Expires:', column4X + 2, yPos + 5);
                        doc.text(`${x.MATDATE ? moment(x.MATDATE).format('MM-DD-YYYY') : ''}`, column4X + 23, yPos + 5);
                        doc.text('ACES:', column4X + 2, yPos + 10);
                        doc.text(`${x.ACES ? moment(x.ACES).format('MM-DD-YYYY') : ''}`, column4X + 23, yPos + 10);
                        doc.text('E. Law:', column4X + 2, yPos + 15);
                        doc.text(`${x.ELaw ? moment(x.ELaw).format('MM-DD-YYYY') : ''}`, column4X + 23, yPos + 15);

                        // Increase yPos for the next staff member and ensure a page break if needed
                        yPos += staffRowHeight;  // Adding space between rows
                    });

                    // Each site starts on a fresh page, but the last one must not
                    // leave an empty page behind it
                    renderedSites++;
                    if (renderedSites < totalSites) {
                        doc.addPage();
                        yPos = 20;
                    }
                })
            });


            // Add page numbers and date at the bottom
            const pageCount = doc.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(BODY_FONT_SIZE);
                doc.text(todayDate, pageWidth - margin - 40, pageHeight - margin - 2); // Date at bottom right
                doc.text(`Page ${i} of ${pageCount}`, margin, pageHeight - margin - 2); // Page number at bottom left
            }

            const pdfBlob = doc.output('blob');
            setPdfBlob(pdfBlob);
        } catch (error) {
            console.log('site info error at index : ', index);
            console.log('site info error at index and data: ', data);
            console.log('site info error', error);
        }

    };





    useEffect(() => {
        if (data) {
            generatePDF();
        }
    }, [data]);

    return null; // This component doesn't render anything, it just generates the PDF
};

export default memo(SiteInfoReportPDF);
